import React, { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Searchable multi-select combobox: a trigger button opens a popover with a search
// box and checkable options; chosen options render as removable badges below.
export default function MultiSelect({ options, value, onChange, placeholder = "Select...", testId }) {
  const [open, setOpen] = useState(false);
  const selected = Array.isArray(value) ? value : [];

  const toggle = (opt) => {
    onChange(selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt]);
  };
  const remove = (opt) => onChange(selected.filter((v) => v !== opt));
  const selectAll = () => onChange(selected.length === options.length ? [] : [...options]);

  return (
    <div className="space-y-2" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={`${testId}-trigger`}
            className="flex h-9 w-full items-center justify-between rounded-md skeu-well px-3 py-2 text-sm text-left"
          >
            <span className={cn("truncate", selected.length === 0 && "text-muted-foreground")}>
              {selected.length === 0 ? placeholder : `${selected.length} of ${options.length} selected`}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search skills..." data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty>No matches.</CommandEmpty>
              <CommandGroup>
                <CommandItem onSelect={selectAll} data-testid={`${testId}-select-all`}>
                  <Check className={cn("mr-2 h-4 w-4", selected.length === options.length && options.length > 0 ? "opacity-100" : "opacity-0")} />
                  {selected.length === options.length && options.length > 0 ? "Clear all" : "Select all"}
                </CommandItem>
                {options.map((opt) => (
                  <CommandItem key={opt} value={opt} onSelect={() => toggle(opt)} data-testid={`${testId}-option-${opt}`}>
                    <Check className={cn("mr-2 h-4 w-4", selected.includes(opt) ? "opacity-100" : "opacity-0")} />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((opt) => (
            <Badge key={opt} variant="secondary" className="gap-1 pr-1 font-normal">
              {opt}
              <button
                type="button"
                onClick={() => remove(opt)}
                data-testid={`${testId}-remove-${opt}`}
                aria-label={`Remove ${opt}`}
                className="ml-0.5 rounded-full hover:bg-black/10 focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
